'use client';

import { createClient } from '@/lib/supabase/client';

export interface PaperPosition {
  id: string;
  symbol: string;
  direction: 'long' | 'short';
  entryPrice: number;
  quantity: number;
  stopLoss: number | null;
  takeProfit: number | null;
  openedAt: string;
  strategy: string;
  currentPrice: number;
}

export interface PaperTrade {
  id: string;
  symbol: string;
  direction: 'long' | 'short';
  entryPrice: number;
  exitPrice: number;
  quantity: number;
  pnl: number;
  pnlPct: number;
  openedAt: string;
  closedAt: string;
  strategy: string;
  exitReason: 'manual' | 'stop_loss' | 'take_profit';
}

export interface PaperPortfolio {
  virtualCash: number;
  initialCash: number;
  positions: PaperPosition[];
  tradeHistory: PaperTrade[];
}

const STORAGE_KEY = 'paper_trading_portfolio';

const DEFAULT_PORTFOLIO: PaperPortfolio = {
  virtualCash: 100000,
  initialCash: 100000,
  positions: [],
  tradeHistory: [],
};

// ─── localStorage helpers ────────────────────────────────────────────────────
function lsLoad(): PaperPortfolio {
  if (typeof window === 'undefined') return DEFAULT_PORTFOLIO;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PORTFOLIO;
    return JSON.parse(raw) as PaperPortfolio;
  } catch {
    return DEFAULT_PORTFOLIO;
  }
}

function lsSave(portfolio: PaperPortfolio): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(portfolio));
}

// ─── Supabase helpers ────────────────────────────────────────────────────────
async function getAuthUser() {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  return { supabase, user };
}

async function getOrCreatePortfolioId(supabase: any, userId: string): Promise<string | null> {
  try {
    const { data: existing } = await supabase
      .from('paper_portfolios')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();

    if (existing) return existing.id;

    const { data: created } = await supabase
      .from('paper_portfolios')
      .insert({ user_id: userId, virtual_cash: 100000, initial_cash: 100000 })
      .select('id')
      .single();

    return created?.id ?? null;
  } catch {
    return null;
  }
}

// ─── Public API ──────────────────────────────────────────────────────────────

export async function loadPortfolioAsync(): Promise<PaperPortfolio> {
  try {
    const { supabase, user } = await getAuthUser();
    if (!user) return lsLoad();

    const { data: portfolioRow } = await supabase
      .from('paper_portfolios')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!portfolioRow) return DEFAULT_PORTFOLIO;

    const { data: positions } = await supabase
      .from('paper_positions')
      .select('*')
      .eq('portfolio_id', portfolioRow.id);

    const { data: trades } = await supabase
      .from('paper_trades')
      .select('*')
      .eq('portfolio_id', portfolioRow.id)
      .order('created_at', { ascending: false });

    const portfolio: PaperPortfolio = {
      virtualCash: Number(portfolioRow.virtual_cash),
      initialCash: Number(portfolioRow.initial_cash),
      positions: (positions || []).map((p: any) => ({
        id: p.id,
        symbol: p.symbol,
        direction: p.direction as 'long' | 'short',
        entryPrice: Number(p.entry_price),
        quantity: Number(p.quantity),
        stopLoss: p.stop_loss != null ? Number(p.stop_loss) : null,
        takeProfit: p.take_profit != null ? Number(p.take_profit) : null,
        openedAt: p.opened_at,
        strategy: p.strategy || '',
        currentPrice: Number(p.current_price),
      })),
      tradeHistory: (trades || []).map((t: any) => ({
        id: t.id,
        symbol: t.symbol,
        direction: t.direction as 'long' | 'short',
        entryPrice: Number(t.entry_price),
        exitPrice: Number(t.exit_price),
        quantity: Number(t.quantity),
        pnl: Number(t.pnl),
        pnlPct: Number(t.pnl_pct),
        openedAt: t.opened_at,
        closedAt: t.closed_at,
        strategy: t.strategy || '',
        exitReason: t.exit_reason as PaperTrade['exitReason'],
      })),
    };

    // Keep localStorage in sync
    lsSave(portfolio);
    return portfolio;
  } catch {
    return lsLoad();
  }
}

export async function savePortfolioAsync(portfolio: PaperPortfolio): Promise<void> {
  lsSave(portfolio);
  try {
    const { supabase, user } = await getAuthUser();
    if (!user) return;

    const portfolioId = await getOrCreatePortfolioId(supabase, user.id);
    if (!portfolioId) return;

    // Update cash balances
    await supabase
      .from('paper_portfolios')
      .update({ virtual_cash: portfolio.virtualCash, initial_cash: portfolio.initialCash })
      .eq('id', portfolioId);

    // Sync positions: delete all and re-insert
    await supabase.from('paper_positions').delete().eq('portfolio_id', portfolioId);
    if (portfolio.positions.length > 0) {
      await supabase.from('paper_positions').insert(
        portfolio.positions.map((p) => ({
          id: p.id,
          portfolio_id: portfolioId,
          user_id: user.id,
          symbol: p.symbol,
          direction: p.direction,
          entry_price: p.entryPrice,
          quantity: p.quantity,
          stop_loss: p.stopLoss,
          take_profit: p.takeProfit,
          opened_at: p.openedAt,
          strategy: p.strategy || '',
          current_price: p.currentPrice,
        }))
      );
    }
  } catch {
    // localStorage already saved above
  }
}

export async function addPaperTradeAsync(portfolioId: string, userId: string, trade: PaperTrade, supabase: any): Promise<void> {
  try {
    await supabase.from('paper_trades').insert({
      id: trade.id,
      portfolio_id: portfolioId,
      user_id: userId,
      symbol: trade.symbol,
      direction: trade.direction,
      entry_price: trade.entryPrice,
      exit_price: trade.exitPrice,
      quantity: trade.quantity,
      pnl: trade.pnl,
      pnl_pct: trade.pnlPct,
      opened_at: trade.openedAt,
      closed_at: trade.closedAt,
      strategy: trade.strategy || '',
      exit_reason: trade.exitReason,
    });
  } catch {
    // ignore
  }
}

export function loadPortfolio(): PaperPortfolio {
  return lsLoad();
}

export function savePortfolio(portfolio: PaperPortfolio): void {
  lsSave(portfolio);
}

export function resetPortfolio(): PaperPortfolio {
  const fresh = { ...DEFAULT_PORTFOLIO, positions: [], tradeHistory: [] };
  lsSave(fresh);
  return fresh;
}

export async function resetPortfolioAsync(): Promise<PaperPortfolio> {
  const fresh = { ...DEFAULT_PORTFOLIO, positions: [], tradeHistory: [] };
  lsSave(fresh);
  try {
    const { supabase, user } = await getAuthUser();
    if (!user) return fresh;

    const portfolioId = await getOrCreatePortfolioId(supabase, user.id);
    if (portfolioId) {
      await supabase.from('paper_positions').delete().eq('portfolio_id', portfolioId);
      await supabase.from('paper_trades').delete().eq('portfolio_id', portfolioId);
      await supabase
        .from('paper_portfolios')
        .update({ virtual_cash: 100000, initial_cash: 100000 })
        .eq('id', portfolioId);
    }
  } catch {
    // localStorage already reset
  }
  return fresh;
}

export function calcPositionPnL(pos: PaperPosition): { pnl: number; pnlPct: number } {
  const diff =
    pos.direction === 'long'
      ? pos.currentPrice - pos.entryPrice
      : pos.entryPrice - pos.currentPrice;
  const pnl = diff * pos.quantity;
  const pnlPct = (diff / pos.entryPrice) * 100;
  return { pnl, pnlPct };
}

export function calcPortfolioStats(portfolio: PaperPortfolio) {
  const openPnL = portfolio.positions.reduce((sum, pos) => sum + calcPositionPnL(pos).pnl, 0);
  const positionsValue = portfolio.positions.reduce(
    (sum, pos) => sum + pos.currentPrice * pos.quantity,
    0
  );
  const totalEquity = portfolio.virtualCash + positionsValue + openPnL;
  const totalReturn = totalEquity - portfolio.initialCash;
  const totalReturnPct = (totalReturn / portfolio.initialCash) * 100;

  const wins = portfolio.tradeHistory.filter((t) => t.pnl > 0).length;
  const total = portfolio.tradeHistory.length;
  const winRate = total > 0 ? (wins / total) * 100 : 0;

  const realizedPnL = portfolio.tradeHistory.reduce((sum, t) => sum + t.pnl, 0);

  return { openPnL, totalEquity, totalReturn, totalReturnPct, winRate, realizedPnL };
}
